<template>
  <div :key="instanceKey" v-loading="loading" class="app-container" style="overflow:auto;position:relative">
    <div>
      <h2 style="width:30%">{{ $t('route.menu_show_orders') }}</h2>
    </div>
    <el-form>
      <div style="display:flex">
        <div style="width:30%;float:left;margin-right:20px">
          <label>{{ $t('common.general') }}</label>
          <div>
            <Select
              v-model="order.domainID"
              :placeholder="$t('common.domain')"
              clearable
              :options="domains"
              value-field="id"
              label-field="name"
              class="search-select"
              style="padding-bottom: 10px;width:100%"
              @input="handleDomainChange($event)"
            >
            </Select>
          </div>
          <div>
            <MDinput
              v-model="order.customerReference"
              :required="true"
              :disabled="!order.domainID"
              type="text"
              :maxlength="32"
              style="padding-bottom: 10px"
            >
              {{ $t('orderLine.customerReference') }} (b.v. SHOWLB.S21.46, SHOWLB.V21.46)
            </MDinput>
          </div>
          <div>
            <MDinput
              v-model="order.consumerName"
              :disabled="!order.domainID"
              type="text"
              :required="false"
              :maxlength="32"
              style="padding-bottom: 10px"
              name="orderLine.consumerName"
            >
              {{ $t('orderLine.consumerName') }}
            </MDinput>
          </div>
          <div style="padding-bottom:10px">
            <Select
              v-model="order.productGroupID"
              :disabled="!order.domainID"
              :placeholder="$t('common.productGroup')"
              clearable
              :options="productGroups"
              value-field="id"
              label-field="description"
              class="search-select"
              @input="handleProductGroupChange($event)"
            />
          </div>
          <div style="padding-bottom:10px">
            <Select
              v-model="order.parcelTypeID"
              :placeholder="defaultParcelTypeDescription || $t('common.parcelType')"
              clearable
              :options="parcelTypes"
              value-field="id"
              label-field="description"
              class="search-select"
            />
          </div>
          <div style="margin-bottom:5px;position:relative">
            <Select
              ref="generalFinishSelect"
              v-model="order.generalFinishID"
              style="display:inline-block"
              :disabled="!order.productGroupID"
              :placeholder="$t('orderLine.generalFinish')"
              clearable
              :options="generalFinishesAvailable"
              value-field="finishID"
              label-field="description"
              :required="true"
              @input="handleGeneralFinishSelection"
            />
            <div
              v-if="finishTypeNeedsMeasurement(order.generalFinishID)"
              style="display:inline-block;width:40%;padding-left:15px"
            >
              <MDinput
                v-model="order.generalFinishMeasurementInCm"
                style="width:60%"
                type="number"
                :placeholder="$t('orderLine.measurement')"
                :disabled="!order.productGroupID"
                :required="false"
                :maxlength="6"
                :precision="1"
                :suffix="'cm'"
              />
            </div>
            <div v-if="finishTypeNeedsProduct(order.generalFinishID)" style="padding-top:5px">
              <Select
                v-model="order.generalFinishProductID"
                :disabled="!order.productGroupID"
                :placeholder="$t('orderLine.product')"
                :options="products.filter(p => p.productTypeID === generalFinishRequiredProductTypeID)"
                value-field="id"
                label-field="description"
                class="search-select"
              />
            </div>
          </div>
        </div>
        <div style="width:30%;float:left;margin-right:20px">
          <label>{{ $t('common.measurements') }}</label>
          <div>
            <create-cutting
              v-if="
                selectedProductGroup && selectedProductGroup.productGroupTypeOption == productGroupTypeOption.Cutting
              "
              @input="assignProperties"
            />
            <create-panel
              v-if="
                selectedProductGroup && selectedProductGroup.productGroupTypeOption == productGroupTypeOption.Curtain
              "
              @input="assignProperties"
            />
            <create-roman-blind
              v-if="
                selectedProductGroup && selectedProductGroup.productGroupTypeOption == productGroupTypeOption.RomanBlind
              "
              @input="assignProperties"
            />
            <create-room-high-curtain
              v-if="
                selectedProductGroup && selectedProductGroup.productGroupTypeOption == productGroupTypeOption.NetCurtain
              "
              @input="assignProperties"
            />
            <create-embrace
              v-if="
                selectedProductGroup && selectedProductGroup.productGroupTypeOption == productGroupTypeOption.Embrace
              "
              @input="assignProperties"
            />
            <generic-order-components style="padding-top:5px" :domain-i-d="order.domainID" @input="assignProperties" />
          </div>
        </div>
        <div style="width:35%;float:left">
          <generic-finish-components v-if="orderHasFinishes" :order="order" :finishes-available="finishesAvailable" />
          <price-table style="margin-top:10px" :domain-i-d="order.domainID" @input="assignProperties" />
          <MDinput v-model="order.remarks" type="text" :required="false" name="order.remarks">
            {{ $t('common.remarks') }}
          </MDinput>
        </div>
      </div>
      <div style="margin-top:10px">
        <div style="width:48%;float:left;margin-right:20px">
          <StoreTable :domain-i-d="order.domainID" @input="assignProperties" />
          &nbsp;
        </div>
        <div style="width:48%;float:right;margin-right:20px;padding-bottom:20px">
          <FabricTable :domain-i-d="order.domainID" @input="assignProperties" />
          &nbsp;
        </div>
      </div>
      <div style="position: absolute;bottom: 0;right: 0;padding-bottom: 10px;padding-right: 20px;">
        <editor-button
          button-translation="order.createSamples"
          action="primary"
          :icon-only="false"
          @click="saveOrder()"
        />
      </div>
    </el-form>
    &nbsp;
  </div>
</template>

<script>
import { ProductGroupTypeOption } from '@/utils/constants';
import CreateCutting from './Components/CreateCutting';
import CreatePanel from './Components/CreatePanel';
import CreateRomanBlind from './Components/CreateRomanBlind';
import CreateRoomHighCurtain from './Components/CreateRoomHighCurtain';
import CreateEmbrace from './Components/CreateEmbrace';
import GenericOrderComponents from './Components/GenericOrderComponents';
import EditorButton from '@/components/crud/EditorButton';
import GenericFinishComponents from './Components/GenericFinishComponents';
import StoreTable from './Components/StoreTable';
import FabricTable from './Components/FabricTable';
import PriceTable from './Components/PriceTable';
import MDinput from '@/components/MDinput';
import Select from '@/components/DSE/Select';
import { FinishTypeOption } from '@/utils/constants';

export default {
  name: 'CreateOrder',
  components: {
    CreateCutting,
    CreatePanel,
    CreateRomanBlind,
    CreateRoomHighCurtain,
    CreateEmbrace,
    GenericOrderComponents,
    GenericFinishComponents,
    EditorButton,
    StoreTable,
    PriceTable,
    MDinput,
    Select,
    FabricTable
  },
  props: {},
  data() {
    return {
      defaultParcelTypeDescription: null,
      loading: true,
      selectedProductGroup: null,
      finishTypeOption: FinishTypeOption,
      productGroupTypeOption: ProductGroupTypeOption,
      order: this.newOrder(),
      finishesAvailable: [],
      instanceKey: 0
    };
  },
  computed: {
    generalFinishesAvailable() {
      return this.finishesAvailable.filter(f => f.finishTypeOption === this.finishTypeOption.General);
    },
    domains() {
      return this.$store.getters['domain/loadedItems'];
    },
    stores() {
      return this.$store.getters['store/loadedItems'].filter(i => i.domainID === this.order.domainID);
    },
    parcelTypes() {
      return this.$store.getters['parcelType/loadedItems'];
    },
    productGroups() {
      return this.$store.getters['productGroup/loadedItems'].filter(
        pg =>
          pg.productGroupTypeOption === this.productGroupTypeOption.NetCurtain ||
          pg.productGroupTypeOption === this.productGroupTypeOption.Curtain ||
          pg.productGroupTypeOption === this.productGroupTypeOption.RomanBlind ||
          pg.productGroupTypeOption === this.productGroupTypeOption.Cutting
      );
    },
    finishes() {
      return this.$store.getters['finish/loadedItems'];
    },
    finishTypes() {
      return this.$store.getters['finishType/loadedItems'];
    },
    products() {
      return this.$store.getters['product/loadedItems'];
    },
    orderHasFinishes() {
      return (
        this.selectedProductGroup &&
        (this.selectedProductGroup.productGroupTypeOption === this.productGroupTypeOption.RomanBlind ||
          this.selectedProductGroup.productGroupTypeOption === this.productGroupTypeOption.NetCurtain ||
          this.selectedProductGroup.productGroupTypeOption === this.productGroupTypeOption.Curtain ||
          this.selectedProductGroup.productGroupTypeOption === this.productGroupTypeOption.CreateShowPanel)
      );
    }
  },
  watch: {},
  async created() {
    await Promise.all([
      this.$store.dispatch('domain/getItems'),
      this.$store.dispatch('productGroup/getItems'),
      this.$store.dispatch('finish/getItems'),
      this.$store.dispatch('finishType/getItems'),
      this.$store.dispatch('parcelType/getItems'),
      this.$store.dispatch('product/getItems'),
      this.$store.dispatch('productType/getItems'),
      this.$store.dispatch('storeType/getItems')
    ]);
    this.loading = false;
  },
  methods: {
    newOrder() {
      return {};
    },
    handleGeneralFinishSelection() {
      this.order.generalFinishProductID = null;
      this.order.generalFinishMeasurementInCm = 0;
      this.$forceUpdate();
    },
    handleDomainChange(id) {
      if (id) {
        this.order.customerReference = 'SHOW' + this.domains.find(d => d.id === id).orderNumberPrefix + '.';
        this.getFinishes();
        this.getDefaultParcelType();
      }
    },
    handleProductGroupChange(selectedID) {
      const order = this.order;
      this.order = {
        consumerName: order.consumerName,
        customerReference: order.customerReference,
        domainID: order.domainID,
        finishedHeightInCm: order.finishedHeightInCm,
        prices: order.prices,
        productGroupID: order.productGroupID,
        remarks: order.remarks,
        showPanelFabricIDs: order.showPanelFabricIDs,
        showPanelStoreIDs: order.showPanelStoreIDs
      };
      this.selectedProductGroup = this.productGroups.find(pg => pg.id === selectedID);
      this.getFinishes();
      this.getDefaultParcelType();
    },
    getDefaultParcelType() {
      const domainID = this.order.domainID;
      const productGroupID = this.order.productGroupID;
      if (!domainID || !productGroupID) {
        this.defaultParcelTypeDescription = null;
        return;
      }
      this.loading = true;
      this.$store
        .dispatch('linkDomainProductGroup/getParcelType', {
          domainID: domainID,
          productGroupID: productGroupID
        })
        .then(data => {
          this.defaultParcelTypeDescription = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getFinishes() {
      if (this.order.domainID && this.order.productGroupID) {
        this.$store
          .dispatch('finishType/getFinishesForDomainAndProductGroup', {
            domainID: this.order.domainID,
            productGroupID: this.order.productGroupID
          })
          .then(data => {
            this.finishesAvailable = data;
          });
      }
    },
    assignProperties(properties) {
      Object.assign(this.order, properties);
    },
    saveOrder() {
      this.loading = true;
      this.$store
        .dispatch('order/createOrder', this.order)
        .then(() => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_created') });
          this.order = this.newOrder();
          this.instanceKey++;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    finishTypeNeedsMeasurement(finishID) {
      let needsMeasurement = false;
      if (finishID) {
        const finishTypeID = this.generalFinishesAvailable.find(f => f.finishID === finishID).finishTypeID;
        const finishType = this.finishTypes.find(ft => ft.id === finishTypeID);
        if (!finishType.measurementRequiredYN) {
          this.order.generalFinishMeasurementInCm = 0;
        }
        needsMeasurement = !!finishType.measurementRequiredYN;
      }
      return needsMeasurement;
    },
    finishTypeNeedsProduct(finishID) {
      let needsProduct = false;
      if (finishID) {
        const finishTypeID = this.generalFinishesAvailable.find(f => f.finishID === finishID).finishTypeID;
        const finishType = this.finishTypes.find(ft => ft.id === finishTypeID);
        this.generalFinishRequiredProductTypeID = finishType.requiredProductTypeID;
        needsProduct = finishType.requiredProductTypeID;
      }
      return needsProduct;
    }
  }
};
</script>
